import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  ResponsiveContainer, ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { TrendingUp, Award, BarChart3, ChevronDown } from 'lucide-react';

export default function CustomerGrowthChart({ api }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/stats/customers-growth?year=${year}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, year]);

  const rows = data?.data || [];
  const bestName = data?.best_month?.name;
  const maxCount = Math.max(1, ...rows.map((r) => r.count));

  const years = [];
  const nowY = new Date().getFullYear();
  for (let y = nowY; y >= nowY - 3; y--) years.push(y);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Clientes cadastrados
            </h3>
            <p className="text-xs text-muted-foreground">Crescimento mensal em {year}</p>
          </div>
          <div className="relative">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="appearance-none text-sm bg-muted/40 border border-border/60 rounded-lg pl-3 pr-8 py-1.5 cursor-pointer hover:border-border"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
          </div>
        </div>

        {/* Mini-cards de resumo */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
          <div className="rounded-xl p-3 bg-gradient-to-br from-blue-500/15 to-blue-600/5 border border-blue-500/20">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-blue-400 font-semibold">
              <BarChart3 className="w-3 h-3" /> Total no ano
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1">{data?.total ?? '—'}</p>
          </div>
          <div className="rounded-xl p-3 bg-gradient-to-br from-emerald-500/15 to-emerald-600/5 border border-emerald-500/20">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-emerald-400 font-semibold">
              <Award className="w-3 h-3" /> Melhor mês
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1">
              {data?.best_month ? `${data.best_month.name} (${data.best_month.count})` : '—'}
            </p>
          </div>
          <div className="rounded-xl p-3 bg-gradient-to-br from-amber-500/15 to-amber-600/5 border border-amber-500/20">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-amber-400 font-semibold">
              <TrendingUp className="w-3 h-3" /> Média mensal
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1">{data?.monthly_avg ?? '—'}</p>
          </div>
        </div>

        {/* Gráfico */}
        <div className="h-[240px] w-full">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Carregando…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 10, right: 6, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/40" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false}
                       stroke="currentColor" className="text-muted-foreground" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false}
                       stroke="currentColor" className="text-muted-foreground" width={30} />
                <Tooltip
                  cursor={{ fill: 'rgba(59,130,246,0.06)' }}
                  contentStyle={{ background: '#0e1524', border: '1px solid #1b2637', borderRadius: 12, fontSize: 12 }}
                  labelStyle={{ color: '#e8eef7' }}
                  formatter={(v) => [`${v} cliente${v === 1 ? '' : 's'}`, 'Cadastrados']}
                />
                <Bar dataKey="count" barSize={26} radius={[6, 6, 0, 0]}>
                  {rows.map((r, i) => (
                    <Cell key={i} fill={r.month === bestName ? '#10b981' : '#3b82f6'}
                          fillOpacity={r.count === 0 ? 0.15 : (r.month === bestName ? 0.95 : 0.55)} />
                  ))}
                </Bar>
                <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2.5}
                      fill="url(#growthArea)" dot={{ r: 3, fill: '#3b82f6' }}
                      activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
